import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { useAutoRefetch } from '@/lib/useAutoRefetch';
import { RoleBadge } from '@/components/common/RoleBadge';
import { MiniBars } from '@/components/common/MiniBars';
import { STAGE_LABEL, ROLE_LABELS, canManageTeam } from '@/lib/roles';
import { TeamDirectorySheetPanel } from '@/components/team/TeamDirectorySheetPanel';
import { TeamMemberDialog } from '@/components/team/TeamMemberDialog';
import { PurgeQueuePanel } from '@/components/team/PurgeQueuePanel';
import { PurgeHistoryPanel } from '@/components/team/PurgeHistoryPanel';
import { AssignProjectDropdown } from '@/components/team/AssignProjectDropdown';
import { BulkActionBar } from '@/components/bulk/BulkActionBar';
import { useBulkSelection } from '@/lib/useBulkSelection';
import { Users, Grid3x3, Mail, UserPlus, Pencil, Trash2, PauseCircle } from 'lucide-react';

const BULK_ROLES = ['owner', 'operations', 'accounts'];
const RECORD_STATE_LABEL = { on_hold: 'On hold', archived: 'Archived' };

const STAGE_ORDER = ['advance_retainer', 'strategy_concept', 'schematic', 'dd', 'tdp', 'construction_admin_closure'];
const MP_STAGE_LABEL = {
  strategy_concept: 'Strategy & Concept', schematic: 'Schematic', dd: 'Design Development',
  tdp: 'TDP', construction_admin_closure: 'Construction Admin & Closure',
};

export default function TeamDirectoryPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [mapping, setMapping] = useState([]);
  const [tab, setTab] = useState('directory');
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [dialogState, setDialogState] = useState({ open: false, mode: 'create', user: null });
  const canManage = canManageTeam(user?.role);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [u, m] = await Promise.all([
          api.get('/users'),
          api.get('/master-projects/for-mapping').catch(() => ({ data: [] })),
        ]);
        setUsers(u.data || []);
        setMapping(m.data || []);
      } catch (e) { toast.error(parseErr(e)); }
      finally { setLoading(false); }
    })();
  }, [reloadKey]);

  useEffect(() => {
    const handler = () => setReloadKey(k => k + 1);
    window.addEventListener('team-directory-refresh', handler);
    return () => window.removeEventListener('team-directory-refresh', handler);
  }, []);
  useAutoRefetch(() => setReloadKey(k => k + 1), { collections: ['users', 'master_projects'] });

  const usersById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);

  const roleBars = useMemo(() => {
    const counts = {};
    users.forEach(u => { counts[u.role] = (counts[u.role] || 0) + 1; });
    return Object.entries(counts)
      .map(([role, count]) => ({ label: ROLE_LABELS[role] || role, count }))
      .sort((a, b) => b.count - a.count);
  }, [users]);

  const loadBars = useMemo(() => {
    const counts = {};
    mapping.forEach(p => {
      const ids = new Set([p.creative_lead_id, p.technical_lead_id, ...(p.team_member_ids || [])].filter(Boolean));
      ids.forEach(id => { counts[id] = (counts[id] || 0) + 1; });
    });
    return Object.entries(counts)
      .map(([id, count]) => ({ label: usersById[id]?.name || 'Unknown', count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [mapping, usersById]);

  return (
    <AppShell breadcrumbs={[{ label: 'Team' }]}>
      <div className="p-6 max-w-[1400px] mx-auto space-y-4 fade-up" data-testid="team-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <Users size={22} />Team
            </h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1">
              Everyone in the company, and who is working on what.
            </p>
          </div>
          <div className="seg" data-testid="team-tabs">
            <button className={tab === 'directory' ? 'active' : ''} onClick={() => setTab('directory')} data-testid="team-tab-directory">
              <Users size={12} className="inline mr-1" />Directory
            </button>
            <button className={tab === 'mapping' ? 'active' : ''} onClick={() => setTab('mapping')} data-testid="team-tab-mapping">
              <Grid3x3 size={12} className="inline mr-1" />Resource Mapping
            </button>
          </div>
          {canManage && tab === 'directory' && (
            <button
              onClick={() => setDialogState({ open: true, mode: 'create', user: null })}
              data-testid="team-add-btn"
              className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[12.5px] font-semibold hover:opacity-90 transition-opacity"
            >
              <UserPlus size={13} />Add teammate
            </button>
          )}
        </div>

        {loading ? (
          <div className="p-8 text-center text-[hsl(var(--foreground-muted))]">Loading…</div>
        ) : (
          <>
            <TeamDirectorySheetPanel />
            {(user?.role === 'owner' || user?.role === 'operations') && (
              <PurgeQueuePanel activeUsers={users} />
            )}
            {user?.role === 'owner' && <PurgeHistoryPanel />}
            {tab === 'directory'
              ? <DirectoryTab users={users} roleBars={roleBars} canManage={canManage} role={user?.role}
                              reload={() => setReloadKey(k => k + 1)}
                              onEdit={(u) => setDialogState({ open: true, mode: 'edit', user: u })}
                              onDelete={async (u) => {
                                if (!window.confirm(`Deactivate ${u.name}? Their assignments are preserved but they will no longer be able to log in.`)) return;
                                try {
                                  await api.delete(`/users/${u.id}`);
                                  toast.success('Teammate deactivated.');
                                  setReloadKey(k => k + 1);
                                } catch (e) { toast.error(parseErr(e)); }
                              }} />
              : <MappingTab mapping={mapping} usersById={usersById} users={users} loadBars={loadBars}
                            role={user?.role} reload={() => setReloadKey(k => k + 1)} />}
          </>
        )}
      </div>

      <TeamMemberDialog
        open={dialogState.open}
        onOpenChange={(v) => setDialogState(s => ({ ...s, open: v }))}
        mode={dialogState.mode}
        user={dialogState.user}
        users={users}
        onSaved={() => setReloadKey(k => k + 1)}
      />
    </AppShell>
  );
}

function DirectoryTab({ users, roleBars, canManage, role, reload, onEdit, onDelete }) {
  const canBulk = BULK_ROLES.includes(role);
  const bulkable = useMemo(() => users.filter(u => !u.is_master_account), [users]);
  const sel = useBulkSelection(bulkable);
  return (
    <div className="space-y-4" data-testid="team-directory">
      <div className="card p-4 elev-1">
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold mb-2">
          People by role · {users.length} total
        </div>
        <MiniBars items={roleBars} testId="team-role-bars" />
      </div>

      {canBulk && (
        <BulkActionBar
          module="users" moduleLabel="Team Members"
          selectedIds={sel.selectedIds} onClear={sel.clear}
          onApplied={reload}
          needsApproval={role !== 'owner'}
          editableFields={['role', 'status', 'department', 'reports_to', 'record_state']}
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {users.map(u => (
          <div key={u.id} className="card card-hover p-4 flex items-start gap-3 group relative" data-testid={`team-card-${u.id}`}>
            {canBulk && !u.is_master_account && (
              <input
                type="checkbox"
                checked={sel.isSelected(u.id)}
                onChange={() => sel.toggle(u.id)}
                aria-label={`Select ${u.name}`}
                data-testid={`team-select-${u.id}`}
                className="absolute top-2.5 left-2.5 h-4 w-4 accent-[hsl(var(--primary))] opacity-70 hover:opacity-100 cursor-pointer"
              />
            )}
            <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold text-[14px] flex-none shadow-sm ${canBulk && !u.is_master_account ? 'ml-4' : ''}`}
                 style={{ background: u.avatar_color || 'hsl(var(--primary))' }}>
              {(u.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <div className="font-heading font-semibold text-[14.5px] truncate flex-1">{u.name}</div>
                {u.status && u.status !== 'active' && (
                  <span className="chip status-cancelled" style={{ fontSize: '10px' }}>{u.status}</span>
                )}
                {u.record_state && u.record_state !== 'active' && (
                  <span className="chip" style={{ fontSize: '10px', background: 'hsl(var(--warning)/.14)', color: 'hsl(var(--warning))' }}>
                    <PauseCircle size={9} className="inline mr-0.5" />{RECORD_STATE_LABEL[u.record_state] || u.record_state}
                  </span>
                )}
              </div>
              <div className="mt-1"><RoleBadge role={u.role} /></div>
              {u.department && (
                <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-1 truncate">{u.department}</div>
              )}
              <a href={`mailto:${u.email}`}
                 className="text-[11.5px] text-[hsl(var(--primary))] flex items-center gap-1 mt-1.5 truncate hover:underline"
                 data-testid={`team-email-${u.id}`}>
                <Mail size={10} className="flex-none" />{u.email}
              </a>
            </div>
            {canManage && !u.is_master_account && (
              <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => onEdit(u)} title="Edit"
                        data-testid={`team-edit-${u.id}`}
                        className="w-6 h-6 grid place-items-center rounded hover:bg-[hsl(var(--surface-2))] text-[hsl(var(--foreground-muted))]">
                  <Pencil size={12} />
                </button>
                <button onClick={() => onDelete(u)} title="Deactivate"
                        data-testid={`team-delete-${u.id}`}
                        className="w-6 h-6 grid place-items-center rounded hover:bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]">
                  <Trash2 size={12} />
                </button>
              </div>
            )}
          </div>
        ))}
        {users.length === 0 && (
          <div className="card p-8 text-center text-[hsl(var(--foreground-muted))] col-span-full" data-testid="team-empty">
            No teammates yet. {canManage && <span>Click <strong>Add teammate</strong> above to start.</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function MappingTab({ mapping, usersById, users, loadBars, role, reload }) {
  const name = (id) => (id && usersById[id]?.name) || null;
  // Group by teammate for the new one-click assign section.
  const teammatesForAssign = useMemo(() => {
    const active = (users || []).filter(u => u.status === 'active' && !u.is_master_account);
    return active.map(u => {
      const currentIds = (mapping || [])
        .filter(p => (p.team_member_ids || []).includes(u.id)
          || p.creative_lead_id === u.id
          || p.technical_lead_id === u.id
          || p.architecture_lead_id === u.id)
        .map(p => p.id);
      return { user: u, currentIds };
    });
  }, [users, mapping]);

  return (
    <div className="space-y-4" data-testid="team-mapping">
      <div className="card p-4 elev-1">
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold mb-2">
          Active projects per person
        </div>
        <MiniBars items={loadBars} testId="team-load-bars" />
      </div>

      {/* -------- one-click assign per teammate (Owner + Leads only) -------- */}
      <div className="card overflow-x-auto elev-1" data-testid="assign-by-teammate">
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'hsl(var(--border))' }}>
          <div>
            <div className="text-[13px] font-semibold">Assign by teammate</div>
            <div className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
              One click adds or removes a project. Only Owners and Leads can assign.
            </div>
          </div>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Teammate</th>
              <th>Role</th>
              <th>Assigned projects</th>
            </tr>
          </thead>
          <tbody>
            {teammatesForAssign.length === 0 && (
              <tr><td colSpan={3} className="text-center py-6 text-[hsl(var(--foreground-muted))] italic">
                No active teammates yet.
              </td></tr>
            )}
            {teammatesForAssign.map(({ user: u, currentIds }) => (
              <tr key={u.id} data-testid={`assign-row-${u.id}`}>
                <td className="font-medium">{u.name}</td>
                <td><RoleBadge role={u.role} /></td>
                <td>
                  <AssignProjectDropdown
                    member={u}
                    projects={mapping}
                    currentIds={currentIds}
                    role={role}
                    onChanged={reload}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* -------- existing per-project mapping table (unchanged) -------- */}
      <div className="card overflow-x-auto elev-1">
        <table className="tbl">
          <thead>
            <tr>
              <th>Project</th>
              <th>Client</th>
              <th>Stage</th>
              <th>Creative lead</th>
              <th>Technical lead</th>
              <th>Team members</th>
            </tr>
          </thead>
          <tbody>
            {mapping.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-[hsl(var(--foreground-muted))] italic">
                No active projects mapped yet — they appear here from Master Project Data.
              </td></tr>
            )}
            {mapping.map(p => (
              <tr key={p.id} data-testid={`mapping-row-${p.id}`}>
                <td className="font-medium">{p.project_name}</td>
                <td>{p.client}</td>
                <td><span className="chip chip-info">{MP_STAGE_LABEL[p.project_stage] || STAGE_LABEL[p.project_stage] || p.project_stage}</span></td>
                <td className="text-[12.5px]">{name(p.creative_lead_id) || <span className="italic text-[hsl(var(--foreground-subtle))]">—</span>}</td>
                <td className="text-[12.5px]">{name(p.technical_lead_id) || <span className="italic text-[hsl(var(--foreground-subtle))]">—</span>}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {(p.team_member_ids || []).map(id => name(id)).filter(Boolean).map(n => (
                      <span key={n} className="chip" style={{ fontSize: '11px' }}>{n}</span>
                    ))}
                    {(p.team_member_ids || []).length === 0 && (
                      <span className="italic text-[12px] text-[hsl(var(--foreground-subtle))]">Unassigned</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
